import React from 'react';
import SarasImage from '../../../../../library/input/image/SarasImage';
import SarasTooltip from '../../../../../library/notifications/SarasTooltip';
import SarasTrans from '../../../../../library/notifications/SarasTrans';
import * as fn from '../../../../../helper/uiFunctions';

const THUMB_WIDTH = 76;
const CARD_HEIGHT = 78;

const parseStyle = (styleText, fallback) => {
    if (!styleText) return fallback;

    try {
        const parsed = JSON.parse(styleText);
        return {
            ...fallback,
            ...parsed
        };
    }
    catch (error) {
        return fallback;
    }
};

const formatMiniDate = (dateText) => {
    if (!dateText) return '';

    const parts = dateText.split('-');
    if (parts.length === 3) {
        return `${parts[1]}-${parts[2]}`;
    }

    return dateText;
};

const buildMetaLine = (qty, planFinishDate) => {
    const bits = [];
    if (qty) bits.push(`Qty ${qty}`);
    if (planFinishDate) bits.push(formatMiniDate(planFinishDate));
    return bits.join('  ');
};

const ProdPlanGridItemMini = ({ viewData, onOpenDocument, onOpenLargeImage }) => {
    const documentCode = viewData?.docCode || viewData?.did || '';
    const qty = viewData?.qty || '';
    const planFinishDate = viewData?.planFinishDate || '';
    const imageUrl = viewData?.itemImage?.url || '';
    const imageName = viewData?.itemImage?.fileName || documentCode;
    const targetItem = viewData?.targetItem || {};
    const status = viewData?.status || {};
    const holdRowId = viewData?.holdRowId || 0;
    const holdStatus = viewData?.holdStatus || '';
    const holdStatusDays = viewData?.holdStatusDays || 0;
    const holdLabel = `On Hold: ${holdStatusDays}d`;
    const priority = viewData?.priority || {};
    const pendingWithQC = viewData?.pendingWithQC === true || viewData?.pendingWithQC === 1;
    const pendingWithQCLabel = viewData?.pendingWithQCLabel || 'Pending with QC';
    const pendingWithRequestor = viewData?.pendingWithRequestor === true || viewData?.pendingWithRequestor === 1;
    const pendingWithRequestorLabel = viewData?.pendingWithRequestorLabel || 'Pending with Requestor';
    const hasPendingChip = pendingWithQC || pendingWithRequestor;

    const statusStyle = parseStyle(status.style, {
        background: '#edf2f7',
        color: '#31435d'
    });

    const targetItemStyle = parseStyle(targetItem.type1Style, {
        background: '#edf2f7',
        color: '#31435d'
    });

    const targetItemType3Style = parseStyle(targetItem.type3Style, {
        background: '#edf2f7',
        color: '#31435d'
    });

    const priorityStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '34px',
        maxWidth: '100%',
        padding: '2px 7px',
        borderRadius: '999px',
        fontSize: '10px',
        lineHeight: '12px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };

    const metaChipStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        maxWidth: '100%',
        padding: '2px 7px',
        borderRadius: '999px',
        fontSize: '10px',
        lineHeight: '12px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };

    const pendingChipStyle = {
        ...metaChipStyle,
        background: '#23b915',
        color: '#fff'
    };

    const holdChipStyle = {
        ...metaChipStyle,
        background: '#ff1744',
        color: '#fff'
    };

    return (
        <div
            className='uiProdPlanMiniCard'
            style={{
                minHeight: `${CARD_HEIGHT}px`
            }}
        >
            <SarasImage
                src={imageUrl}
                alt={imageName}
                className='uiProdPlanMiniCardImage'
                height={`${CARD_HEIGHT}px`}
                width={`${THUMB_WIDTH}px`}
                onClick={onOpenLargeImage}
                style={{ cursor: onOpenLargeImage ? 'pointer' : 'default' }}
            />

            <div className='uiProdPlanMiniCardContent'>
                <div style={{ minWidth: '0', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div
                        onClick={onOpenDocument}
                        style={{
                            fontWeight: 700,
                            color: '#31435d',
                            lineHeight: '1.15',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: onOpenDocument ? 'pointer' : 'default'
                        }}
                    >
                        <div className='uiFlexBox'>
                            <div>
                                {documentCode}
                            </div>
                            <div>
                                {priority.name ? (
                                    <SarasTooltip title={priority.name} tooltipAlign='bottom-start'>
                                    <span
                                        className={priority.class || ''}
                                        style={priorityStyle}
                                        title={priority.nn}
                                    >
                                        {priority.nn}
                                    </span>
                                    </SarasTooltip>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            fontSize: '12px',
                            color: '#6a788e',
                            lineHeight: '1.15',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {buildMetaLine(qty, planFinishDate)}
                    </div>
                </div>
                {(status.name || targetItem.type1 || targetItem.type3 || hasPendingChip) ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: '0' }}>
                        {targetItem.type1 ? (
                            <SarasTooltip title={targetItem.type1} tooltipAlign='bottom-start'>
                            <span
                                style={{
                                    ...targetItemStyle,
                                    ...metaChipStyle
                                }}
                                title={targetItem.type1}
                            >
                                {targetItem.type1}
                            </span>
                            </SarasTooltip>
                        ) : null}
                        {targetItem.type3 ? (
                            <SarasTooltip title={targetItem.type3} tooltipAlign='bottom-start'>
                            <span
                                style={{
                                    ...targetItemType3Style,
                                    ...metaChipStyle
                                }}
                                title={targetItem.type3}
                            >
                                {targetItem.type3NN ? targetItem.type3NN : targetItem.type3}
                            </span>
                            </SarasTooltip>
                        ) : null}
                        {pendingWithQC ? (
                            <SarasTooltip title={pendingWithQCLabel} tooltipAlign='bottom-start'>
                                <span
                                    style={pendingChipStyle}
                                >
                                    PWQ
                                </span>
                            </SarasTooltip>
                        ) : null}
                        {pendingWithRequestor ? (
                            <SarasTooltip title={pendingWithRequestorLabel} tooltipAlign='bottom-start'>
                                <span
                                    style={pendingChipStyle}
                                >
                                    PWR
                                </span>
                            </SarasTooltip>
                        ) : null}
                        {!hasPendingChip && !fn.compareNumber(holdRowId, 0) ? (
                            <SarasTooltip title={
                                <>
                                    <div><SarasTrans value='Reason' />: <SarasTrans value={holdStatus} /></div>
                                    {
                                        !fn.compareNumber(holdStatusDays, 0)
                                        ? <div><SarasTrans value='Since' />: {holdStatusDays} <SarasTrans value='days' /></div>
                                        : null
                                    }
                                </>
                            } tooltipAlign='bottom-start'>
                                <span
                                    className='uiChip uiChipRed'
                                    style={holdChipStyle}
                                >
                                    <SarasTrans value={holdLabel} />
                                </span>
                            </SarasTooltip>
                        ) : null}
                        {!hasPendingChip && fn.compareNumber(holdRowId, 0) && status.name ? (
                            <SarasTooltip title={status.name} tooltipAlign='bottom-start'>
                            <span
                                style={{
                                    ...statusStyle,
                                    ...metaChipStyle
                                }}
                                title={status.name}
                            >
                                {status.name}
                            </span>
                            </SarasTooltip>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default ProdPlanGridItemMini;
